/**
 * Utility to determine whether a mapping is a true literal/constant value
 * (i.e. the target value does not come from any source column).
 *
 * The backend `is_constant_transform` heuristic sometimes misclassifies
 * expressions that reference columns (e.g. `DISTINCT(ARM)`) as
 * `transformation_type: "constant"`.  This helper inspects the expression
 * text to guard against that.
 *
 * True literals: `LITERAL('unknown')`, `'Active'`, `NULL`, `42`, `UUID()`
 * NOT literals:  `DISTINCT(ARM)`, `COUNT(AE_ID)`, `CASE WHEN ...`
 */

/** transformation_type values that the backend sets for inputless mappings. */
const CONSTANT_TYPES = new Set(['constant', 'literal', 'set_constant', 'set_null']);

/** Patterns that are definitely literal/constant values. */
const LITERAL_EXPR_RE =
  /^(?:LITERAL\s*\(|'[^']*'$|NULL$|TRUE$|FALSE$|-?\d+(?:\.\d+)?$|UUID\(\)$|GENERATE_UUID\(\)$)/i;

/** SQL keywords that indicate the expression references columns — NOT a constant. */
const COLUMN_REF_KEYWORDS_RE =
  /\b(?:CASE|WHEN|CONCAT|COALESCE|DISTINCT|SUM|AVG|COUNT|MIN|MAX|UPPER|LOWER|TRIM|CAST|SUBSTRING|REPLACE|IF|IIF|NULLIF|OVER|PARTITION|RANK|ROW_NUMBER|DENSE_RANK|LAG|LEAD|NVL|DECODE|CONVERT)\b/i;

/**
 * Returns `true` when the mapping represents a hardcoded literal value
 * and should NOT display a source field / source→target edge.
 */
export const isTrueLiteralMapping = (
  transformationType: string,
  transformationExpr: string,
): boolean => {
  const ttype = (transformationType || '').toLowerCase();
  // If backend says it's not constant/literal, trust it
  if (!CONSTANT_TYPES.has(ttype)) return false;

  const expr = (transformationExpr || '').trim();
  // No expression text — fall back to the type flag
  if (!expr) return true;

  // If expression contains SQL column-referencing keywords, it's NOT a literal
  if (COLUMN_REF_KEYWORDS_RE.test(expr)) return false;

  // If expression matches known literal patterns, it IS a literal
  if (LITERAL_EXPR_RE.test(expr)) return true;

  // Expressions with parentheses likely call functions on columns
  if (expr.includes('(')) return false;

  // Fallback: trust the backend type
  return true;
};
